import { useState, type FormEvent } from "react";
import { ErroreApi, autenticazione } from "../api";
import { TestaPagina } from "../componenti/TestaPagina";
import { CampoPassword } from "../componenti/CampoPassword";
import { Sigla } from "../componenti/Marchio";
import { useSessione } from "../sessione";

const LUNGHEZZA_MINIMA = 12;

/** Vale per due situazioni diverse con lo stesso modulo.
 *
 * `obbligato` e' il primo accesso: la password l'ha scelta chi ha creato
 * l'utenza, quindi la conoscono in due, e finche' non cambia non si va da
 * nessuna parte. Senza `obbligato` e' il cambio volontario dal menu.
 *
 * Il backend chiude tutte le altre sessioni della persona quando la password
 * cambia, questa esclusa: una password cambiata perche' si teme che qualcuno
 * l'abbia vista non serve a niente se chi l'ha vista resta dentro. */
export function CambiaPassword({ obbligato = false }: { obbligato?: boolean }) {
  const { rileggi } = useSessione();
  const [attuale, setAttuale] = useState("");
  const [nuova, setNuova] = useState("");
  const [conferma, setConferma] = useState("");
  const [errore, setErrore] = useState<string | null>(null);
  const [fatto, setFatto] = useState(false);
  const [inCorso, setInCorso] = useState(false);

  async function invia(evento: FormEvent) {
    evento.preventDefault();
    setErrore(null);

    // Le due verifiche che si possono fare qui si fanno qui, non per
    // sostituire quelle del server ma per non far aspettare un giro di rete
    // a chi ha solo sbagliato a ribattere. La regola vera resta di la'.
    if (nuova !== conferma) {
      setErrore("Le due password nuove non coincidono.");
      return;
    }
    if (nuova.length < LUNGHEZZA_MINIMA) {
      setErrore(
        `La password nuova deve essere lunga almeno ${LUNGHEZZA_MINIMA} caratteri.`,
      );
      return;
    }

    setInCorso(true);
    try {
      await autenticazione.cambiaPassword(attuale, nuova);
      setFatto(true);
      setAttuale("");
      setNuova("");
      setConferma("");
      await rileggi();
    } catch (e) {
      setErrore(
        e instanceof ErroreApi ? e.message : "Cambio non riuscito. Riprova.",
      );
    } finally {
      setInCorso(false);
    }
  }

  const modulo = (
    <form className="modulo" onSubmit={invia} noValidate>
      <div className="campo">
        <label htmlFor="attuale">Password attuale</label>
        <CampoPassword
          id="attuale"
          value={attuale}
          autoComplete="current-password"
          autoFocus
          required
          onChange={(e) => setAttuale(e.target.value)}
        />
      </div>

      <div className="campo">
        <label htmlFor="nuova">Password nuova</label>
        <CampoPassword
          id="nuova"
          value={nuova}
          autoComplete="new-password"
          required
          onChange={(e) => setNuova(e.target.value)}
        />
        <p className="aiuto">
          Almeno {LUNGHEZZA_MINIMA} caratteri. Una frase che ricordi vale più di
          una parola con i simboli dentro.
        </p>
      </div>

      <div className="campo">
        <label htmlFor="conferma">Ripeti la password nuova</label>
        <CampoPassword
          id="conferma"
          value={conferma}
          autoComplete="new-password"
          required
          onChange={(e) => setConferma(e.target.value)}
        />
      </div>

      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}
      {fatto && !obbligato && (
        <p className="aiuto" role="status">
          Password cambiata. Le altre sessioni aperte sono state chiuse.
        </p>
      )}

      <div>
        <button
          className="tasto tasto-principale"
          type="submit"
          disabled={inCorso}
        >
          {inCorso ? "Sto salvando" : "Cambia password"}
        </button>
      </div>
    </form>
  );

  if (obbligato) {
    return (
      <main className="portineria">
        <div className="portineria-foglio">
          <Sigla bicolore className="accesso-sigla" />
          <p className="accesso-studio">Studio Ceruzzi</p>
          <h1>Scegli la password</h1>
          <p className="portineria-sottotitolo">
            Quella con cui hai fatto l'accesso l'ha scelta qualcun altro, quindi la
            conoscete in due. Cambiala e si va avanti.
          </p>
          {modulo}
        </div>
      </main>
    );
  }

  return (
    <>
      <TestaPagina titolo="Cambia password" />
      <p className="aiuto">
        Cambiandola, le altre sessioni aperte a tuo nome vengono chiuse. Questa
        resta aperta.
      </p>
      <div className="colonna-stretta">{modulo}</div>
    </>
  );
}
